// What each of the app's cord figures hangs, for the size it's laid out at
// (see ./engine.ts). Pure functions of their data, so they're tested
// without a canvas.
import { KNOT } from "../lib/knot";
import { Status } from "../types/Tracking";
import { WorkType } from "../types/Work";
import {
  Build,
  CordSpec,
  Dye,
  KnotLabel,
  KnotSpec,
  StripeSpec,
} from "./engine";

/** Each media type's dye. */
export const DYE: Record<WorkType, Dye> = {
  book: "--dye-book",
  movie: "--dye-movie",
  tv: "--dye-tv",
  game: "--dye-game",
  music: "--dye-music",
};

/** A place on a progress cord: finished, under way (or next), or to come. */
export type Slot = "done" | "next" | "open";

// A tight knot of three turns is about 27 knot units long.
const TIGHT_LENGTH = 27;

/**
 * A progress cord, run edge to edge (a Continue card's, a season's): a slot
 * for each episode or volume, with a tight knot on each one finished and a
 * loose one, `wound` turns of three, on the next. `inset` is the padding
 * the slots keep from the edges. Slots too narrow for a knot tie smaller
 * knots on a thinner cord.
 */
export const progressCord =
  (dye: Dye, slots: Slot[], inset: number, wound = 3): Build =>
  (W, H) => {
    const ext = 14;
    const span = W + 2 * ext;
    const y = H / 2;
    const room = (W - 2 * inset) / slots.length;
    const w = Math.max(2, Math.min(5, (6 * 0.9 * room) / TIGHT_LENGTH));
    const k = w < 5 ? w / 6 : undefined;
    const at = (i: number) => (ext + inset + room * (i + 0.5)) / span;
    const knots: KnotSpec[] = [];
    slots.forEach((slot, i) => {
      if (slot === "done")
        knots.push({ id: `k${i}`, s: at(i), n: 3, k, delay: 120 + i * 60 });
      else if (slot === "next")
        knots.push({
          id: `k${i}`,
          s: at(i),
          n: 3,
          p: wound,
          c: 0,
          k,
          delay: 150 + i * 60,
        });
    });
    return {
      cords: [
        {
          id: "cord",
          dye,
          w,
          len: span * 1.0006,
          pins: { 0: { x: -ext, y }, 1: { x: W + ext, y } },
          knots,
        },
      ],
    };
  };

/**
 * The steps of a process (a set-up, an import): one undyed cord run edge to
 * edge through them all, a slot each. The step you're on has a long knot,
 * loose; moving on pulls it tight (the same knot, by its step's id) and a
 * loose one appears on the next. Those to come are bare cord, where their
 * knots will go. The figure reaches `bleed` past its host on every side,
 * room for the cord to sway; the slots share the host's width.
 */
export const stepsCord =
  (slots: Slot[], bleed = 0): Build =>
  (W, H) => {
    const ext = 14;
    const span = W + 2 * ext;
    const y = H / 2;
    const room = (W - 2 * bleed) / slots.length;
    const knots: KnotSpec[] = [];
    slots.forEach((slot, i) => {
      if (slot === "open") return;
      knots.push({
        id: `k${i}`,
        s: (ext + bleed + room * (i + 0.5)) / span,
        n: 3,
        k: 1.3,
        ...(slot === "next" ? { c: 0 } : {}),
        delay: 120 + i * 60,
      });
    });
    return {
      cords: [
        {
          id: "cord",
          dye: "--cord-plain",
          w: 4,
          len: span * 1.0002,
          // Run across a page: light, or it sags.
          weight: 0.15,
          pins: { 0: { x: -ext, y }, 1: { x: W + ext, y } },
          knots,
        },
      ],
    };
  };

/** A title finished in the year, woven into its month. */
export type YearTitle = {
  id: string;
  /** 0 (January) to 11. */
  month: number;
  type: WorkType;
  /** A return to it: a re-read, a rewatch, a replay. */
  again: boolean;
  label: KnotLabel;
};

// Picks a title weaves in: two of its dye, and a crimson one for a return.
const PICKS = 2;
// The closest a month packs its picks before it lumps its titles by type, px.
const MIN_PITCH = 3;
// The most picks of its dye a lumped stripe weaves in.
const LUMP_PICKS = 6;

// A stripe to weave into a month: one title, or a crowded month's titles of
// one type, lumped.
type Weft = {
  id: string;
  type: WorkType;
  picks: number;
  again: boolean;
  label: KnotLabel;
};

// How many picks and gaps a month's stripes take, a gap between each.
const slotsOf = (wefts: Weft[]) =>
  wefts.reduce((sum, w) => sum + w.picks + (w.again ? 1 : 0) + 1, -1);

/**
 * The year as a woven band, January to December across the figure: every
 * title finished weaves in a stripe of its type's dye in its month's
 * twelfth, in order, with a crimson thread when it was a return to it. A
 * busy month packs its picks closer; one too crowded to point at a title in
 * lumps its titles of a type into one stripe, wider the more there are,
 * labelled by `lump`. The months to come are bare, their warp faint: `now`
 * is this month (0..11), 11 for a year gone by and -1 for one still to
 * come.
 */
export const yearWeave =
  (
    titles: YearTitle[],
    now: number,
    lump: (titles: YearTitle[]) => KnotLabel,
  ): Build =>
  (W, H) => {
    // A phone's figure is shorter, and so is its band.
    const small = H < 80;
    const y = H / 2;
    const col = W / 12;
    const room = col - 10;
    const stripes: StripeSpec[] = [];
    let n = 0;
    for (let m = 0; m < 12; m++) {
      const month = titles.filter((t) => t.month === m);
      if (!month.length) continue;
      const one = (t: YearTitle): Weft => ({ ...t, picks: PICKS });
      let wefts = month.map(one);
      if (room / slotsOf(wefts) < MIN_PITCH) {
        wefts = (Object.keys(DYE) as WorkType[]).flatMap((type): Weft[] => {
          const of = month.filter((t) => t.type === type);
          if (of.length < 2) return of.map(one);
          return [
            {
              id: `${type}-${m}`,
              type,
              // A pick wider each time the count doubles.
              picks: Math.min(LUMP_PICKS, 1 + Math.ceil(Math.log2(of.length))),
              again: of.some((t) => t.again),
              label: lump(of),
            },
          ];
        });
        // Still too close: the widest stripes give up picks first.
        while (room / slotsOf(wefts) < MIN_PITCH) {
          const widest = wefts.reduce((a, b) => (b.picks > a.picks ? b : a));
          if (widest.picks === PICKS) break;
          widest.picks--;
        }
      }
      const slots = slotsOf(wefts);
      const pitch = Math.min(small ? 4.5 : 5.5, room / slots);
      // Picks packed closer are finer, so they stay apart.
      const pick = Math.min(small ? 3 : 3.6, pitch * 0.67);
      let x = col * m + (col - (slots - 1) * pitch) / 2;
      for (const w of wefts) {
        const dyes: Dye[] = Array(w.picks).fill(DYE[w.type]);
        if (w.again) dyes.push("--accent");
        const x0 = x;
        const at = dyes.map((_, j) => (x0 + j * pitch) / W);
        x += (dyes.length + 1) * pitch;
        stripes.push({
          id: w.id,
          at,
          dyes,
          pick,
          label: w.label,
          delay: 300 + n++ * 140,
        });
      }
    }
    return {
      cords: [
        {
          id: "year",
          dye: "--cord-plain",
          w: 4,
          len: W * 1.0002,
          pins: { 0: { x: 0, y }, 1: { x: W, y } },
          band: {
            width: small ? 34 : 44,
            warps: small ? 7 : 9,
            stripes,
            fade: now >= 11 ? undefined : (now + 1) / 12,
          },
        },
      ],
    };
  };

/** A timeline row, measured down the cord: where its middle is, and what happened. */
export type TimelineKnot = {
  id: string;
  y: number;
  type: WorkType;
  kind: "finished" | "dropped" | "started";
  /** A finish's time through: a turn more for each return (the diary's). */
  times?: number;
};

// The longest timeline cord swayed fully, px.
const SWAY_SPAN = 800;

/**
 * The timeline's cord, running down beside its rows with a knot at each:
 * tight for a finish, loose for a start.
 */
export const timelineCord =
  (rows: TimelineKnot[]): Build =>
  (W, H) => {
    const ext = 30;
    const x = W / 2;
    const span = H + 2 * ext;
    return {
      cords: [
        {
          id: "cord",
          dye: "--accent",
          w: 4,
          // A cord has as many points however long it is, so a long one's
          // links are long: the same sway bends them further, and its slack
          // pools at the bottom and buckles it. A long timeline's is swayed
          // less and kept taut, to sway no wider than a short one's, and
          // nothing carries it out of its column.
          len: span + Math.min(span * 0.0005, 0.4),
          sway: Math.min(1, SWAY_SPAN / span) ** 2,
          reach: x,
          pins: { 0: { x, y: -ext }, 1: { x, y: H + ext } },
          knots: rows.map((r, i) => ({
            id: r.id,
            s: (r.y + ext) / span,
            n: Math.min(2 + (r.times ?? 1), 9),
            c: r.kind === "finished" ? 1 : 0,
            alpha: r.kind === "dropped" ? LET_GO : undefined,
            dye: DYE[r.type],
            delay: 200 + i * 90,
          })),
        },
      ],
    };
  };

/** Where the `q`th of `count` covers is pegged along a clothesline. */
export const pegAt = (q: number, count: number) => (q + 1) / (count + 1);

/**
 * A clothesline strung across the figure, `y` down it, with `count` covers
 * pegged along it (their `data-at`, see pegAt), weighted where they hang.
 */
export const clothesline =
  (count: number, dye: Dye = "--text", y = 22): Build =>
  (W) => {
    const ext = 28;
    const span = W + 2 * ext;
    return {
      cords: [
        {
          id: "line",
          dye,
          w: 3,
          len: span * 1.012,
          damp: 0.97,
          heavy: Array.from({ length: count }, (_, q): [number, number] => [
            pegAt(q, count),
            6,
          ]),
          pins: { 0: { x: -ext, y: y + 4 }, 1: { x: W + ext, y: y - 4 } },
        },
      ],
    };
  };

// How much a dropped title's cord or knot fades: let go, not gone.
const LET_GO = 0.7;

// How much longer than its span a cord let go is: it falls away slack.
const LET_GO_SLACK = 1.015;

/**
 * The install card's cord: crimson, run across the card and off both its
 * edges, level with what sits on it. Bare until the app's installed, when
 * the brand's knot, the app's own icon, ties `at` px in; let go (Not now),
 * it falls slack.
 */
export const installCord =
  (tied: boolean, letGo: boolean, at: number): Build =>
  (W, H) => {
    const ext = 24;
    const span = W + 2 * ext;
    const y = H / 2;
    return {
      cords: [
        {
          id: "cord",
          dye: "--accent",
          w: 5,
          len: span * (letGo ? LET_GO_SLACK : 1.0006),
          pins: { 0: { x: -ext, y }, 1: { x: W + ext, y } },
          // As big as the app's icon draws it.
          knots: tied
            ? [{ id: "app", s: (ext + at) / span, n: 3, k: 1.4, delay: 120 }]
            : [],
        },
      ],
    };
  };

/** A strand of what an app brings: in its type's dye, plied for two. */
export type Strand = { dye: Dye; ply?: Dye };

/**
 * An import's splice, a guide's hero: a strand for each thing an app
 * brings, fanned out from the app's side (off the left edge) and run into
 * your library's crimson cord, joined there with a knot. The library runs
 * on off the right edge. The splice sits halfway, where the guide's second
 * step starts, or on a phone further on, to give the strands room.
 *
 * An import under way (the welcome's) runs beads along the strands. The
 * knot is the join, tight whether it's under way or done.
 */
export const spliceCords =
  (strands: Strand[], { running = false } = {}): Build =>
  (W, H) => {
    const ext = 30;
    const S = { x: W * (W < 600 ? 0.62 : 0.5), y: H / 2 };
    const n = strands.length;
    // The knot that joins them: big enough to bind them all.
    const k = 1.6;
    // Each strand comes into the knot at its own height, stacked within it,
    // so the knot binds them side by side rather than at a point.
    const gap = Math.min(3, (KNOT.h * k - 6) / Math.max(1, n - 1));
    const cords: CordSpec[] = strands.map(({ dye, ply }, i) => {
      const a = {
        x: -ext,
        y: n > 1 ? H * (0.14 + (0.72 * i) / (n - 1)) : S.y,
      };
      const e = { x: S.x, y: S.y + (i - (n - 1) / 2) * gap };
      return {
        id: `strand${i}`,
        dye,
        ply,
        // Two strands plied are about twice as wide as one.
        w: ply ? 2.5 : 4,
        // Taut: any slack and a strand sags, and comes up into the knot
        // from below rather than level with it.
        len: Math.hypot(e.x - a.x, e.y - a.y),
        pins: { 0: a, 1: e },
        ...(running && { beads: true }),
      };
    });
    // Your library's cord starts inside the knot, which sits right where the
    // strands meet, and leaves it level and taut, so the knot lies along
    // the strands rather than tipping into a sag. It runs on off the right
    // edge. (Never shorter than its span: pulled tighter than that, the
    // engine lets go of a cord's tethers and it falls slack.)
    const lead = 12;
    // Nothing coming in yet (the waitlist before anything's ticked): one
    // cord, edge to edge, its knot already tied where the strands will join.
    if (!n) {
      const len = W + 2 * ext;
      return {
        cords: [
          {
            id: "library",
            dye: "--accent",
            w: 7,
            len,
            pins: { 0: { x: -ext, y: S.y }, 1: { x: W + ext, y: S.y } },
            knots: [
              { id: "splice", s: (S.x + ext) / len, n: 3, k, delay: 300 },
            ],
          },
        ],
      };
    }
    const len = W + ext - (S.x - lead);
    // Last, so the knot is drawn over every strand.
    cords.push({
      id: "library",
      dye: "--accent",
      w: 7,
      len,
      pins: {
        0: { x: S.x - lead, y: S.y, dir: 0 },
        1: { x: W + ext, y: S.y },
      },
      // The join: covers where the strands meet, tied whatever comes in.
      knots: [{ id: "splice", s: lead / len, n: 3, k, delay: 300 }],
    });
    return { cords };
  };

/**
 * An app as its bundle, on the guides' index: a strand for each thing it
 * brings, spread across the figure on the app's side (the left, inside
 * `pad`), drawn together and bound with one undyed knot (the app, which has
 * no type), then fanned out closer as they run on off the right edge.
 */
export const bundleCords =
  (strands: Strand[], pad: number): Build =>
  (W, H) => {
    const ext = 20;
    const n = strands.length;
    const K = { x: W * 0.46, y: H / 2 };
    const top = pad + 6;
    const bot = H - pad - 6;
    const lerp = (a: number, b: number, u: number) => a + (b - a) * u;
    // Where each strand is held where they're drawn together, 0..1.
    const mids: number[] = [];
    const cords: CordSpec[] = strands.map(({ dye, ply }, i) => {
      const u = n > 1 ? i / (n - 1) : 0.5;
      const a = { x: -ext, y: lerp(top, bot, u) };
      const b = {
        x: W + ext,
        y: lerp(lerp(top, K.y, 0.4), lerp(bot, K.y, 0.4), u),
      };
      const m = { x: K.x, y: K.y + (i - (n - 1) / 2) * 2.6 };
      const l1 = Math.hypot(m.x - a.x, m.y - a.y);
      const l2 = Math.hypot(b.x - m.x, b.y - m.y);
      const mid = Math.round((l1 / (l1 + l2)) * 1e4) / 1e4;
      mids.push(mid);
      return {
        id: `strand${i}`,
        dye,
        ply,
        w: ply ? 2 : 3,
        len: (l1 + l2) * 1.0008,
        pins: { 0: a, [mid]: m, 1: b },
      };
    });
    // The middle strand carries the knot, drawn last so it binds the rest.
    const i = Math.floor(n / 2);
    const [middle] = cords.splice(i, 1);
    middle.knots = [
      { id: "bind", s: mids[i], n: 3, k: 1.5, dye: "--cord-plain", delay: 250 },
    ];
    cords.push(middle);
    return { cords };
  };

// How much longer than the span a dropped work's cord is: enough to sag.
const SLACK = 1.004;

/** A work's knot: how many turns, how many are on, how tight. */
export type WorkKnot = { n: number; p: number; c: number };

// A khipu's long knot counted up to nine turns.
const MOST_TURNS = 9;

/**
 * The knot a work's cord carries for where it stands, having been finished
 * `times` times: the first finish ties three turns tight and every return
 * adds one. Under way, it's loose with the next turn part-wound; dropped, it
 * stays loose. Nothing to show for a title only wanted and never finished.
 */
export const workKnot = (status: Status, times: number): WorkKnot | null => {
  const tied = Math.min(2 + Math.max(times, 1), MOST_TURNS);
  switch (status) {
    case "want":
      return times > 0 ? { n: tied, p: tied, c: 1 } : null;
    case "in_progress": {
      if (times === 0) return { n: 3, p: 2, c: 0 };
      const n = Math.min(3 + times, MOST_TURNS);
      return { n, p: n - 0.55, c: 0 };
    }
    case "completed":
      return { n: tied, p: tied, c: 1 };
    case "dropped":
      return { n: tied, p: tied, c: 0 };
  }
};

/**
 * A work page's cord, run corner to corner across the figure, with the
 * work's knot on it (see workKnot), or bare. A dropped title's cord is let
 * go: slack, it sags between its ends, and it fades a little.
 */
export const workCord =
  (
    dye: Dye,
    knot: WorkKnot | null,
    label?: KnotLabel,
    dropped = false,
  ): Build =>
  (W, H) => {
    const ext = 60;
    const y0 = H * 0.78;
    const y1 = H * 0.26;
    return {
      cords: [
        {
          id: "cord",
          dye,
          alpha: dropped ? LET_GO : 1,
          w: W < 600 ? 11 : 15,
          len: Math.hypot(W + 2 * ext, y0 - y1) * (dropped ? SLACK : 1.0006),
          pins: { 0: { x: -ext, y: y0 }, 1: { x: W + ext, y: y1 } },
          knots: knot
            ? [{ id: "knot", s: 0.6, ...knot, grow: true, delay: 200, label }]
            : [],
        },
      ],
    };
  };

/**
 * A count to knot on a string: of a type, in its dye (how many titles of it
 * someone has finished), or of anything else, in a dye of its own.
 */
export type Tally = { count: number } & ({ type: WorkType } | { dye: Dye });

// How long a knot of nine turns is, at the logo's size.
const NINE = 8 * KNOT.pitchTight + KNOT.pitchLoose;

/**
 * A profile's khipu: a string across the figure for each type, one to a
 * row, knotted with how many titles of that type are finished (or with any
 * other tally, in its dye), place by place as a khipu records a number,
 * read left to right: `places` columns,
 * the highest place first and the ones last, each digit a long knot of that
 * many turns (a zero, none).
 */
export const profileKhipu =
  (tallies: Tally[], places: number): Build =>
  (W, H) => {
    const row = H / tallies.length;
    const col = W / places;
    // Knots as big as a nine fits in its column.
    const k = Math.min(1.15, (col - 12) / NINE);
    return {
      cords: tallies.map((tally, j): CordSpec => {
        const y = row * (j + 0.5);
        const digits = String(tally.count).padStart(places, "0").split("");
        return {
          id: `p${j}`,
          dye: "type" in tally ? DYE[tally.type] : tally.dye,
          w: W < 360 ? 4 : 5,
          len: (W - 4) * 1.0004,
          pins: { 0: { x: 2, y }, 1: { x: W - 2, y } },
          knots: digits.flatMap((d, q) =>
            d === "0"
              ? []
              : [
                  {
                    id: `d${q}`,
                    s: (col * (q + 0.5) - 2) / (W - 4),
                    n: Number(d),
                    k,
                    delay: 250 + j * 170 + q * 150,
                  },
                ],
          ),
        };
      }),
    };
  };

/** A post's sections beside it, `y` down the figure: read, reading, or to come. */
export type SectionKnot = {
  id: string;
  y: number;
  at: "read" | "here" | "ahead";
};

/**
 * A post's contents as a cord running down beside its section names: a
 * tight knot for each section read, a loose one where you are, and a ghost
 * of a knot, faint, for each still ahead.
 */
export const contentsCord =
  (rows: SectionKnot[], bleed: number): Build =>
  (W, H) => {
    // Held at the host's top and bottom: the bleed is room for the knots to
    // swell as they tie, not more cord.
    const x = W / 2;
    const span = H - 2 * bleed;
    return {
      cords: [
        {
          id: "cord",
          dye: "--cord-plain",
          w: 3,
          len: span * 1.0004,
          sway: 0.4,
          reach: x,
          pins: { 0: { x, y: bleed }, 1: { x, y: H - bleed } },
          knots: rows.map((r, i) => ({
            id: r.id,
            s: r.y / span,
            n: 3,
            c: r.at === "here" ? 0 : 1,
            alpha: r.at === "ahead" ? 0.28 : undefined,
            dye: r.at === "here" ? "--accent" : undefined,
            delay: 120 + i * 60,
          })),
        },
      ],
    };
  };

/**
 * A post's sections across a phone's screen, pinned under its header as you
 * read: the contents cord turned on its side, a knot spaced evenly for each
 * section, tied as `contentsCord` ties them. `bleed` is the figure's, which
 * the knots keep inside the host.
 */
export const readingCord =
  (ats: SectionKnot["at"][], bleed = 0): Build =>
  (W, H) => {
    const ext = 14;
    const span = W + 2 * ext;
    const y = H / 2;
    const room = (W - 2 * bleed) / ats.length;
    return {
      cords: [
        {
          id: "cord",
          dye: "--cord-plain",
          w: 3,
          len: span * 1.0002,
          weight: 0.15,
          pins: { 0: { x: -ext, y }, 1: { x: W + ext, y } },
          knots: ats.map((at, i) => ({
            id: `k${i}`,
            s: (ext + bleed + room * (i + 0.5)) / span,
            n: 3,
            k: 0.8,
            c: at === "here" ? 0 : 1,
            alpha: at === "ahead" ? 0.28 : undefined,
            dye: at === "here" ? "--accent" : undefined,
            delay: 120 + i * 60,
          })),
        },
      ],
    };
  };

/** A goal's mark (ADR 0062): reached, the one under way, or ahead. */
export type GoalMark = { at: number; slot: Slot; label: KnotLabel };

/**
 * A year's goals as a khipu of strings, one a goal, each in its type's dye
 * and held edge to edge: its length the goal, and a knot at each mark
 * along it (a quarter, half, three quarters, done) tied tight once a
 * finish reached it, loose on the one under way, and none yet where the
 * rest will go. The knots keep `bleed` inside the host's edges.
 */
export const goalsKhipu =
  (goals: { type: WorkType; marks: GoalMark[] }[], bleed: number): Build =>
  (W, H) => {
    const row = H / goals.length;
    const span = W - 2 * bleed;
    return {
      cords: goals.map(({ type, marks }, j): CordSpec => {
        const y = row * (j + 0.5);
        return {
          id: `g${type}`,
          dye: DYE[type],
          w: 4,
          len: (W + 4) * 1.0004,
          pins: { 0: { x: -2, y }, 1: { x: W + 2, y } },
          knots: marks.flatMap((mark, i): KnotSpec[] =>
            mark.slot === "open"
              ? []
              : [
                  {
                    id: `m${i}`,
                    s: (bleed + 2 + span * mark.at) / (W + 4),
                    n: 3,
                    ...(mark.slot === "next" ? { p: 2, c: 0 } : {}),
                    delay: 200 + j * 170 + i * 120,
                    label: mark.label,
                  },
                ],
          ),
        };
      }),
    };
  };
